// Taşeron portalı — giriş ekranı. Tek input (e-posta VEYA telefon; normalize
// backend'de) + parola → `/auth/supplier/login` (api-contract §5.4). Yanıt firma
// sayısına göre dallanır: terminal (tek firma) → panele; workspace_select (çok
// firma) → firma seçim ekranına (transit token router state ile taşınır).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Spinner,
  type SupplierAuthResponseDto,
  resolveApiErrorMessage,
  supplierLogin,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const schema = useMemo(
    () =>
      z.object({
        emailOrPhone: z.string().min(1, t('validation.identifierRequired')),
        password: z.string().min(1, t('validation.passwordRequired')),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { emailOrPhone: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => supplierLogin(values),
    onSuccess: (dto: SupplierAuthResponseDto) => {
      if (dto.tokenState === 'workspace_select') {
        // Çok firma → transit token + firma listesi seçim ekranına taşınır
        // (store'a yazılmaz; api-contract §5.4). State yoksa ekran login'e döner.
        navigate('/select-workspace', {
          replace: true,
          state: { transitToken: dto.accessToken, companies: dto.companies ?? [] },
        });
        return;
      }
      // Tek firma terminal → oturum servis içinde kuruldu; panele geç.
      navigate('/', { replace: true });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h1">{t('login.title')}</CardTitle>
        <CardDescription className="carbon:mb-6">{t('login.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((values) => mutation.mutate(values))}
          noValidate
        >
          {mutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          <Field
            htmlFor="emailOrPhone"
            label={t('login.identifier')}
            error={errors.emailOrPhone?.message}
            required
          >
            <Input
              id="emailOrPhone"
              autoComplete="username"
              placeholder={t('login.identifierPlaceholder')}
              invalid={Boolean(errors.emailOrPhone)}
              {...register('emailOrPhone')}
            />
          </Field>

          <Field
            htmlFor="password"
            label={t('login.password')}
            error={errors.password?.message}
            required
          >
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={Boolean(errors.password)}
              {...register('password')}
            />
          </Field>

          <Button type="submit" loading={mutation.isPending} className="mt-1 w-full">
            {mutation.isPending ? <Spinner label={t('login.submit')} /> : t('login.submit')}
          </Button>

          <div className="text-center text-sm">
            <Link to="/forgot-password" className="text-primary hover:underline">
              {t('login.forgotPassword')}
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
